/**
 * AI design generation, provider-independent part.
 *
 * The model never draws pixels or writes full template documents. It fills a short "design
 * spec" — which layout, palette, font, picture and texts — and the gallery layout builders
 * (all built with stacks) turn it into a template. Small output = cheap and fast requests, and
 * every result is a valid, adaptive template.
 */
import { z } from 'zod';
import { BUILTIN_FONTS } from './fonts';
import { GALLERY, PALETTES, type GalleryLocale, type PaletteId } from './gallery';
import { ILLUSTRATIONS, type GalleryImageId } from './gallery/illustrations';
import { PHOTOS } from './gallery/photos';
import { LAYOUTS, type Copy, type LayoutId } from './gallery/layouts';
import type { TemplateDoc } from './template';

/** Layouts the AI designer may pick (photo layouts need stock photos and are gallery-only). */
export const AI_LAYOUTS: Partial<Record<LayoutId, string>> = {
  split:
    'Text column next to a picture; becomes a column in tall sizes. Product/offer posts. Uses kicker, title, subtitle and either badge+price or cta.',
  hero: 'Full-bleed picture with a dark overlay and a centred message. Announcements, travel, holidays. Uses kicker, title, subtitle, cta.',
  card: 'Picture on top, text card below. Stories, product cards. Uses kicker, title, subtitle, price, cta.',
  quote:
    'Big quote with the author. Testimonials, quotes. Title = the quote; uses author, role. No picture needed.',
  event:
    'Date badge, title, bullet details and a button. Webinars, events, vacancies. kicker = date/time, details = 1-3 short lines, cta.',
  number:
    'Huge number (discount, milestone) with a short message. badge = the number like -50% or 10K; uses kicker, title, subtitle, cta.',
  match:
    'Sports match announcement on a stadium scene with two team crests and VS. details = [home team, away team], kicker = league/tournament, badge = short label (LIVE, Final), subtitle = date and time, brand = TV channel or club, cta.',
  result:
    'Final score of a match with two crests. details = [home team, away team], title = the score like "2 : 1", badge = "Full time", kicker = league, subtitle = goal scorers, brand.',
  strip:
    'One-line web banner (728x90, 970x250, 320x50): brand, title, short subtitle, cta. Only for very wide sizes.',
};

const LAYOUT_IDS = Object.keys(AI_LAYOUTS) as [LayoutId, ...LayoutId[]];
const PALETTE_IDS = Object.keys(PALETTES) as [PaletteId, ...PaletteId[]];
const ILLUSTRATION_IDS = Object.keys(ILLUSTRATIONS);
const PHOTO_IDS = Object.keys(PHOTOS);
const IMAGE_IDS = [...PHOTO_IDS, ...ILLUSTRATION_IDS] as [GalleryImageId, ...GalleryImageId[]];
/** Fonts the model may pick for headlines: built-in and with Cyrillic, so every language works. */
export const AI_FONTS = BUILTIN_FONTS.filter((f) => f.cyrillic && f.category !== 'monospace').map(
  (f) => f.family,
);

const text = (max: number) => z.string().trim().max(max);

export const aiDesignSpecSchema = z.object({
  name: text(80),
  layout: z.enum(LAYOUT_IDS),
  palette: z.enum(PALETTE_IDS),
  titleFont: z.string().max(64).optional(),
  /** Illustration id, or "none". */
  image: z.string().max(40).optional(),
  copy: z.object({
    kicker: text(60).optional(),
    title: text(120),
    subtitle: text(200).optional(),
    cta: text(40).optional(),
    price: text(24).optional(),
    badge: text(16).optional(),
    author: text(60).optional(),
    role: text(80).optional(),
    brand: text(30).optional(),
    details: z.array(text(120)).max(4).optional(),
  }),
});
export type AiDesignSpec = z.infer<typeof aiDesignSpecSchema>;

/** Very wide sizes only work as one-line banners, and one-line banners only work there. */
function fitLayout(layout: LayoutId, width: number, height: number): LayoutId {
  const wide = width / height >= 3;
  if (wide) return 'strip';
  return layout === 'strip' ? 'split' : layout;
}

/** Turn a (validated) spec into a template document of the requested size. */
export function buildFromSpec(spec: AiDesignSpec, width: number, height: number): TemplateDoc {
  const palette = { ...PALETTES[spec.palette] };
  if (spec.titleFont && AI_FONTS.includes(spec.titleFont)) palette.titleFont = spec.titleFont;
  const image =
    spec.image && (IMAGE_IDS as string[]).includes(spec.image)
      ? (spec.image as GalleryImageId)
      : undefined;
  const copy = Object.fromEntries(
    Object.entries(spec.copy).filter(
      ([, v]) => v !== undefined && v !== '' && !(Array.isArray(v) && !v.length),
    ),
  ) as unknown as Copy;
  return LAYOUTS[fitLayout(spec.layout, width, height)]({
    width,
    height,
    palette,
    copy,
    image,
  });
}

/** Character limits asked of the model: in the prompt and as maxLength in the answer schema. */
export const AI_TEXT_LIMITS = {
  name: 40,
  kicker: 30,
  title: 50,
  subtitle: 70,
  cta: 20,
  price: 16,
  badge: 10,
  author: 40,
  role: 60,
  brand: 20,
  detail: 60,
} as const;

/** What the normaliser accepts: a little above the requested limits, then it shortens. */
const COPY_LIMITS: Record<string, number> = {
  kicker: 40,
  title: 70,
  subtitle: 90,
  cta: 28,
  price: 24,
  badge: 16,
  author: 60,
  role: 80,
  brand: 30,
};

/** Shorten at a sentence end, else at a word boundary; never in the middle of a word. */
const clip = (v: unknown, max: number): string | undefined => {
  if (typeof v !== 'string' && typeof v !== 'number') return undefined;
  const s = String(v).replace(/\s+/g, ' ').trim();
  if (!s) return undefined;
  if (s.length <= max) return s;
  const head = s.slice(0, max);
  const sentence = head.match(/^(.*[.!?])\s/)?.[1];
  if (sentence && sentence.length >= max * 0.4) return sentence;
  const space = head.lastIndexOf(' ');
  const cut = space > max * 0.4 ? head.slice(0, space) : head.slice(0, max - 1);
  return `${cut.replace(/[\s,;:–—-]+$/, '')}…`;
};

/**
 * Bring one model variant into shape instead of rejecting it for small slips: over-long texts
 * are shortened, unknown layouts/palettes fall back to defaults, unknown fields are dropped.
 * Only a variant without a title is unusable.
 */
function normalizeVariant(raw: unknown): AiDesignSpec | null {
  if (!raw || typeof raw !== 'object') return null;
  const v = raw as Record<string, unknown>;
  const c = (v.copy && typeof v.copy === 'object' ? v.copy : v) as Record<string, unknown>;
  const copy: Record<string, unknown> = {};
  for (const [key, max] of Object.entries(COPY_LIMITS)) {
    const value = clip(c[key], max);
    if (value) copy[key] = value;
  }
  if (Array.isArray(c.details)) {
    const details = c.details
      .map((d) => clip(d, 80))
      .filter(Boolean)
      .slice(0, 3);
    if (details.length) copy.details = details;
  }
  if (!copy.title) return null;
  const layout = (LAYOUT_IDS as string[]).includes(String(v.layout)) ? v.layout : 'split';
  const palette = (PALETTE_IDS as string[]).includes(String(v.palette)) ? v.palette : 'indigo';
  const spec = {
    name: clip(v.name, 80) ?? String(copy.title).slice(0, 80),
    layout,
    palette,
    titleFont: typeof v.titleFont === 'string' ? v.titleFont.slice(0, 64) : undefined,
    image: typeof v.image === 'string' ? v.image.slice(0, 40) : undefined,
    copy,
  };
  const parsed = aiDesignSpecSchema.safeParse(spec);
  return parsed.success ? parsed.data : null;
}

/** Parse what the model returned (lenient, see `normalizeVariant`). */
export function parseAiDesigns(raw: unknown): AiDesignSpec[] {
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as { variants?: unknown }).variants)
      ? (raw as { variants: unknown[] }).variants
      : [];
  return list
    .slice(0, 6)
    .map(normalizeVariant)
    .filter((v): v is AiDesignSpec => v !== null);
}

const LANGUAGE: Record<GalleryLocale, string> = {
  en: 'English',
  uk: 'Ukrainian',
  pl: 'Polish',
  ro: 'Romanian',
  es: 'Spanish',
  ru: 'Russian',
};

function paletteLine(id: PaletteId): string {
  const p = PALETTES[id];
  const bg =
    p.background.type === 'solid'
      ? p.background.color
      : p.background.stops.map((s) => s.color).join('→');
  return `- ${id}: background ${bg}, text ${p.title}, accent ${p.accent}, fonts ${p.titleFont}/${p.bodyFont}`;
}

/** A few gallery templates as spec examples (the model learns the style of good output). */
function exampleSpecs(): string {
  const ids = ['summer-sale', 'webinar', 'testimonial', 'flash-sale'];
  return GALLERY.filter((e) => ids.includes(e.id))
    .map((e) => {
      const copy = Object.fromEntries(
        Object.entries(e.copy).map(([k, v]) => [k, (v as Record<string, unknown>).en]),
      );
      return JSON.stringify({
        name: e.name.en,
        layout: e.layout,
        palette: Object.entries(PALETTES).find(([, p]) => p === e.palette)?.[0],
        image: e.image ?? 'none',
        copy,
      });
    })
    .join('\n');
}

/**
 * System instructions: static for every request (so providers can cache them as a prompt
 * prefix) and generated from code, so new layouts, palettes or fonts are known automatically.
 */
export function aiDesignSystemPrompt(): string {
  return [
    'You are a senior marketing designer working in a banner editor. You do not draw: you choose',
    'one of the existing layouts, a colour palette, a headline font and a picture, and write',
    'short, persuasive texts. The editor builds the banner from your choices.',
    '',
    'Layouts:',
    ...Object.entries(AI_LAYOUTS).map(([id, d]) => `- ${id}: ${d}`),
    '',
    'Palettes:',
    ...PALETTE_IDS.map(paletteLine),
    '',
    `Headline fonts (optional, otherwise the palette font): ${AI_FONTS.join(', ')}.`,
    'Pictures (field "image"; prefer a fitting photo, use "none" when nothing fits):',
    ...Object.entries(PHOTOS).map(([id, p]) => `- ${id}: photo, ${p.alt}`),
    `- flat vector illustrations: ${ILLUSTRATION_IDS.join(', ')}`,
    '',
    'Rules:',
    '- Make every variant clearly different: another layout and/or palette and another angle in the copy.',
    '- This is a banner, not an article: people glance at it for 2-3 seconds. Apart from the title,',
    '  the whole banner carries at most ~15 words. Cut every word that is not essential.',
    '- Title: 2-6 words, no trailing period. Kicker: 1-3 words. CTA: 1-3 words (a verb).',
    '- Subtitle: at most 8 words — one benefit or one fact (date, place, offer). Never two sentences,',
    '  never a description or a story. Leave it out when the title already says enough.',
    '- Bad subtitle: "Support our team in this decisive encounter on the pitch. Witness history in the',
    '  making with every goal." Good subtitle: "June 14 · Olympic Stadium, Kyiv".',
    `- Hard length limits in characters: ${Object.entries(AI_TEXT_LIMITS)
      .map(([k, v]) => `${k} ${v}`)
      .join(', ')}. Banners have little room: shorter is better.`,
    '- Use only facts from the brief (prices, dates, discounts, brand); never invent prices or dates.',
    '- Fill only the fields the chosen layout uses; leave the others out.',
    '- Write all texts in the requested language. "name" is a short internal name of the variant.',
    '',
    'Examples of good variants:',
    exampleSpecs(),
  ].join('\n');
}

export function aiDesignUserPrompt(input: {
  brief: string;
  width: number;
  height: number;
  locale: GalleryLocale;
  count: number;
}): string {
  const shape =
    input.width / input.height >= 3
      ? 'a very wide one-line web banner (use the "strip" layout)'
      : input.width > input.height * 1.15
        ? 'landscape'
        : input.height > input.width * 1.15
          ? 'portrait / story'
          : 'square';
  return [
    `Banner size: ${input.width}x${input.height} px (${shape}).`,
    `Language of all texts: ${LANGUAGE[input.locale]}.`,
    `Give ${input.count} variants.`,
    '',
    'Brief from the user:',
    input.brief,
  ].join('\n');
}

/** JSON schema of the answer, in the OpenAPI subset understood by structured-output APIs. */
export function aiDesignJsonSchema(count: number) {
  // maxLength keeps small models from running away with one field (seen: a 8k-token subtitle)
  const str = (key: keyof typeof AI_TEXT_LIMITS, description?: string) => ({
    type: 'STRING',
    maxLength: AI_TEXT_LIMITS[key],
    ...(description ? { description } : {}),
  });
  return {
    type: 'OBJECT',
    properties: {
      variants: {
        type: 'ARRAY',
        minItems: count,
        maxItems: count,
        items: {
          type: 'OBJECT',
          properties: {
            name: str('name'),
            layout: { type: 'STRING', enum: LAYOUT_IDS },
            palette: { type: 'STRING', enum: PALETTE_IDS },
            titleFont: { type: 'STRING', enum: AI_FONTS },
            image: { type: 'STRING', enum: [...IMAGE_IDS, 'none'] },
            copy: {
              type: 'OBJECT',
              properties: {
                kicker: str('kicker', '2-4 words above the title'),
                title: str('title', '2-6 words'),
                subtitle: str('subtitle', 'one short sentence'),
                cta: str('cta', '1-3 words'),
                price: str('price'),
                badge: str('badge', 'e.g. -30% or NEW'),
                author: str('author'),
                role: str('role'),
                brand: str('brand'),
                details: { type: 'ARRAY', items: str('detail'), maxItems: 3 },
              },
              required: ['title'],
            },
          },
          required: ['name', 'layout', 'palette', 'image', 'copy'],
        },
      },
    },
    required: ['variants'],
  };
}

// ---------------------------------------------------------------- text variants

export const aiCopyResponseSchema = z.object({
  variants: z.array(z.string().trim().min(1).max(500)).min(1).max(12),
});

export function aiCopySystemPrompt(): string {
  return [
    'You are a marketing copywriter. Rewrite one text of a banner into alternative versions for',
    'A/B tests. Keep the meaning and every fact (numbers, prices, dates, names); vary the angle,',
    'wording and emotion. Keep a similar length (never more than 1.5x) so it still fits the design.',
    'Write in the requested language. Return only the texts.',
  ].join('\n');
}

export function aiCopyUserPrompt(input: {
  text: string;
  role?: string;
  context?: string;
  locale: GalleryLocale;
  count: number;
}): string {
  return [
    `Language: ${LANGUAGE[input.locale]}.`,
    `Give ${input.count} alternatives.`,
    input.role ? `This text is the "${input.role}" of the banner.` : '',
    input.context ? `Other texts on the banner: ${input.context}` : '',
    '',
    'Text:',
    input.text,
  ]
    .filter((l) => l !== '')
    .join('\n');
}

/** `maxLength` per text keeps variants close to the original length (and the answer bounded). */
export function aiCopyJsonSchema(count: number, maxLength = 300) {
  return {
    type: 'OBJECT',
    properties: {
      variants: {
        type: 'ARRAY',
        items: { type: 'STRING', maxLength },
        minItems: count,
        maxItems: count,
      },
    },
    required: ['variants'],
  };
}

/** Offline stand-in for a model (tests, development without an API key): gallery-based specs. */
export function mockSpecs(brief: string, count: number, locale: GalleryLocale): AiDesignSpec[] {
  const words = brief.trim().split(/\s+/).slice(0, 5).join(' ') || 'New collection';
  const picks = [
    'summer-sale',
    'flash-sale',
    'new-collection',
    'announcement',
    'webinar',
    'testimonial',
  ];
  return picks.slice(0, count).map((id, i) => {
    const entry = GALLERY.find((e) => e.id === id)!;
    const copy = Object.fromEntries(
      Object.entries(entry.copy).map(([k, v]) => [k, (v as Record<string, unknown>)[locale]]),
    ) as AiDesignSpec['copy'];
    return {
      name: `${entry.name[locale]} ${i + 1}`,
      layout: entry.layout,
      palette: Object.entries(PALETTES).find(([, p]) => p === entry.palette)![0] as PaletteId,
      image: entry.image ?? 'none',
      copy: { ...copy, title: i === 0 ? words.slice(0, 120) : copy.title },
    };
  });
}

/**
 * Best-effort repair of a JSON answer cut off mid-way (output limit): closes an open string and
 * all open brackets, dropping a dangling key or comma. Complete variants survive; an over-long
 * text is kept up to the cut (the normaliser shortens it).
 */
export function repairTruncatedJson(text: string): unknown {
  const stack: string[] = [];
  let inString = false;
  let escaped = false;
  for (const ch of text) {
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === '{' || ch === '[') stack.push(ch === '{' ? '}' : ']');
    else if (ch === '}' || ch === ']') stack.pop();
  }
  let fixed = text;
  // Close an open string (a dangling escape backslash is dropped first)
  if (inString) fixed = (escaped ? fixed.slice(0, -1) : fixed) + '"';
  // A cut right after a key, a colon or a comma leaves nothing usable to close
  fixed = fixed.replace(/,\s*"[^"]*"\s*:?\s*$/, '').replace(/[,:]\s*$/, '');
  fixed += stack.reverse().join('');
  try {
    return JSON.parse(fixed);
  } catch {
    return null;
  }
}
